import {Trans} from '@lingui/macro';
import {isEqual, isUndefined, uniqBy} from 'lodash';
import React, {memo, useEffect, useState} from 'react';
import {createSelector} from 'reselect';
import styled, {css} from 'styled-components';

import {EventHandler} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Dropdown, DropdownProps} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {InteractiveList} from '../../../../../../libs/shared/ui/src/components/interactiveList/interactiveList';
import {
  isWildcardToken,
  SearchToken,
  SuggestionSelectHandler,
} from '../../../../../../srcCommon/inboxes/search/searchHelpers';
import {ModifiersEnum} from '../../../../../../srcCommon/inboxes/search/searchTypes';
import {buildTeammateDisplayName} from '../../../../../helpers/modelHelpers';
import {buildTagSearchUrl, SearchTagStatusesEnum} from '../../../../../helpers/models/tagHelpers';
import {useAppSelector} from '../../../../../helpers/reduxHelpers';
import {ApiRecipientContactMatches} from '../../../../../models/api/recipientMatchModel';
import {ApiTags, TagBehaviorsEnum} from '../../../../../models/api/tagModel';
import {InboxesSearchLocationsEnum} from '../../../../../models/url/inboxesUrlModel';
import {buildRecipientContactMatchSearchUrl} from '../../../../../services/recipientMatches';
import {
  getUserOwnedInboxes,
  getUserTeamParticipatingInboxes,
} from '../../../../../store/data/inboxes/inboxesSelectors';
import {
  getFlatActiveTeammates,
  getFlatOtherActiveTeammates,
  getUser,
} from '../../../../../store/data/teammates/teammatesSelectors';
import {InboxesParameters} from '../../../../../store/panels/inboxes/parameters/inboxesParameters';
import {trackSearchDropdownExpand} from '../../searchTrackingHelpers';
import {InboxesSearchSuggestionsDropdownAccountAsync} from './inboxesSearchSuggestionsDropdownAccountAsync';
import {
  InboxesSearchSuggestionsBaseProps,
  InboxesSuggestionsDropdownItemRecipientAvatar,
  InboxesSuggestionsDropdownItemTeammateAvatar,
} from './inboxesSearchSuggestionsDropdownCommons';
import {InboxesSearchSuggestionsDropdownExpandButton} from './inboxesSearchSuggestionsDropdownExpandButton';
import {InboxesSearchSuggestionsDropdownModifiersContainer} from './inboxesSearchSuggestionsDropdownModifiersContainer';
import {InboxesSearchSuggestionsDropdownProperties} from './inboxesSearchSuggestionsDropdownProperties';
import {InboxesSearchSuggestionsDropdownQuery} from './inboxesSearchSuggestionsDropdownQuery';
import {InboxesSearchSuggestionsDropdownQuickFilters} from './inboxesSearchSuggestionsDropdownQuickFilters';
import {InboxesSearchSuggestionsDropdownResourceAsync} from './inboxesSearchSuggestionsDropdownResourceAsync';
import {InboxesSearchSuggestionsDropdownResourceStore} from './inboxesSearchSuggestionsDropdownResourceStore';
import {
  InboxesSearchSuggestionsDropdownSmartMix,
  renderTagResource,
} from './inboxesSearchSuggestionsDropdownSmartMix';
import {InboxesSearchSuggestionsDropdownTopResults} from './inboxesSearchSuggestionsDropdownTopResults';

/*
 * Props.
 */

interface InboxesSearchSuggestionsDropdownProps extends DropdownProps {
  queryHash?: string;
  query?: string;
  activeToken?: SearchToken;
  namespace?: string;
  parameters: InboxesParameters;
  selectedSearchFilter: InboxesSearchLocationsEnum;
  onSuggestionSelect: SuggestionSelectHandler;
  hasSearchAccountFilter: boolean;
}

/*
 * Style.
 */

const StyledSearchList = styled(InteractiveList)`
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: auto;
`;

const StyledScrollableDiv = styled.div<{$addBorder: boolean}>`
  overflow: auto;

  ${(p) =>
    p.$addBorder &&
    css`
      border-bottom: 1px solid ${p.theme.greys.shade30};
    `}
`;

/*
 * Component.
 */

export const InboxesSearchSuggestionsDropdown = memo<InboxesSearchSuggestionsDropdownProps>((props) => {
  const {
    queryHash,
    query,
    activeToken,
    namespace,
    parameters,
    selectedSearchFilter,
    onSuggestionSelect,
    hasSearchAccountFilter,
    ...dropdownProps
  } = props;

  const user = useAppSelector(getUser);
  const teammates = useAppSelector(getFlatOtherActiveTeammates);

  const shouldSelectFirst = Boolean(query && query.trim().length > 0);

  const [isExpanded, setIsExpanded] = useState(false);
  const onToggleExpand: EventHandler = (event) => {
    // Keep the dropdown open.
    event.preventDefault();

    // Track.
    trackSearchDropdownExpand({
      queryHash,
      expand: !isExpanded,
    });

    // Toggle.
    setIsExpanded((wasExpanded) => !wasExpanded);
  };

  const [stateQuery, setStateQuery] = useState(query);
  useEffect(() => {
    // If the new token is wilcard and is a "+", expand.
    if (activeToken && isWildcardToken(activeToken, query) && activeToken.value === '+') {
      setIsExpanded(true);
      setStateQuery(query);
    } else if (!isEqual(query, stateQuery)) {
      setIsExpanded(false);
      setStateQuery(query);
    }
  }, [activeToken, query, stateQuery]);

  if (!activeToken) {
    // We need to set `isExclusive` to false here to be able to click on the filters dropdown button.
    return (
      <Dropdown isExclusive={false} {...dropdownProps}>
        <StyledSearchList filterValue={query} shouldSelectWithTab shouldSelectFirst={shouldSelectFirst}>
          <InboxesSearchSuggestionsDropdownQuery query={query} onSuggestionSelect={onSuggestionSelect} />
          <InboxesSearchSuggestionsDropdownTopResults
            user={user}
            teammates={teammates}
            query={query}
            activeToken={{}}
            parameters={parameters}
            selectedSearchFilter={selectedSearchFilter}
            onSuggestionSelect={onSuggestionSelect}
          />
        </StyledSearchList>
      </Dropdown>
    );
  }

  // Create new props to pass down as we're now sure that `activeToken` is defined.
  const suggestionProps: InboxesSearchSuggestionsBaseProps = {
    query,
    activeToken,
    onSuggestionSelect,
  };

  /* eslint-disable react/jsx-props-no-spreading */

  // We need to set `isExclusive` to false here to be able to click on the filters dropdown button.
  return (
    <Dropdown isExclusive={false} {...dropdownProps}>
      <StyledSearchList filterValue={query} shouldSelectWithTab shouldSelectFirst={shouldSelectFirst}>
        <StyledScrollableDiv $addBorder={!isUndefined(query) && query.length > 0}>
          <InboxesSearchSuggestionsDropdownQuery query={query} onSuggestionSelect={onSuggestionSelect} />
          <InboxesSearchSuggestionsDropdownQuickFilters {...suggestionProps} user={user} />
          {isExpanded && <InboxesSearchSuggestionsDropdownModifiersContainer {...suggestionProps} />}
        </StyledScrollableDiv>
        <InboxesSearchSuggestionsDropdownExpandButton
          {...suggestionProps}
          isExpanded={isExpanded}
          onToggleExpand={onToggleExpand}
        />
        {!isExpanded && !isUndefined(activeToken.value) && (
          <>
            <InboxesSearchSuggestionsDropdownSmartMix
              {...suggestionProps}
              teammates={[user, ...teammates]}
              namespace={namespace}
            />
            <InboxesSearchSuggestionsDropdownResourceStore
              {...suggestionProps}
              itemsSelector={getUserInboxes}
              modifiers={[ModifiersEnum.INBOX]}
              valueKey="alias"
              primaryKey="name"
              secondaryKey="alias"
              sectionTitle={<Trans id="inboxes-search-inboxes-suggestions">Inboxes</Trans>}
              icon="inboxSmall"
            />
            <InboxesSearchSuggestionsDropdownResourceStore
              {...suggestionProps}
              itemsSelector={getEnhancedTeammates}
              modifiers={[
                ModifiersEnum.AUTHOR,
                ModifiersEnum.ASSIGNEE,
                ModifiersEnum.PARTICIPANT,
                ModifiersEnum.MENTION,
                ModifiersEnum.COMMENTER,
              ]}
              valueKey="alias"
              primaryKey="displayName"
              secondaryKey="alias"
              sectionTitle={<Trans id="inboxes-search-teammates-suggestions">Teammates</Trans>}
              renderIcon={(teammate) => <InboxesSuggestionsDropdownItemTeammateAvatar teammate={teammate} />}
            />
            <InboxesSearchSuggestionsDropdownResourceAsync
              {...suggestionProps}
              urlBuilder={(company, q) =>
                buildTagSearchUrl(company, q, {
                  offset: 0,
                  limit: 20,
                  behaviors: [TagBehaviorsEnum.CAN_SUGGEST],
                  status: SearchTagStatusesEnum.ALL,
                })
              }
              apiType={ApiTags}
              modifiers={[ModifiersEnum.TAG]}
              valueKey="alias"
              primaryKey="name"
              secondaryKey="alias"
              sectionTitle={<Trans id="inboxes-search-tags-suggestions">Tags</Trans>}
              renderItem={renderTagResource}
            />
            <InboxesSearchSuggestionsDropdownResourceAsync
              {...suggestionProps}
              urlBuilder={(company, q) =>
                buildRecipientContactMatchSearchUrl(company, q, {
                  preferredNamespace: namespace,
                })
              }
              apiType={ApiRecipientContactMatches}
              transform={(items) => uniqBy(items, (item) => item.handle).slice(0, 20)}
              modifiers={[
                ModifiersEnum.RECIPIENT,
                ModifiersEnum.TO,
                ModifiersEnum.FROM,
                ModifiersEnum.CC,
                ModifiersEnum.BCC,
              ]}
              valueKey="handle"
              primaryKey="name"
              secondaryKey="handle"
              sectionTitle={<Trans id="inboxes-search-recipients-suggestions">Recipients</Trans>}
              renderIcon={(recipient) => (
                <InboxesSuggestionsDropdownItemRecipientAvatar recipient={recipient} />
              )}
            />
            {hasSearchAccountFilter && <InboxesSearchSuggestionsDropdownAccountAsync {...suggestionProps} />}
            <InboxesSearchSuggestionsDropdownProperties {...suggestionProps} />
            <InboxesSearchSuggestionsDropdownTopResults
              {...suggestionProps}
              user={user}
              teammates={teammates}
              parameters={parameters}
              selectedSearchFilter={selectedSearchFilter}
            />
          </>
        )}
      </StyledSearchList>
    </Dropdown>
  );
}, isEqual);

/*
 * Helpers.
 */

const getUserInboxes = createSelector(
  [getUserOwnedInboxes, getUserTeamParticipatingInboxes],
  (userOwnedInboxes, userTeamParticipatingInboxes) => userOwnedInboxes.concat(userTeamParticipatingInboxes),
);

const getEnhancedTeammates = createSelector(getFlatActiveTeammates, (teammates) =>
  teammates.map((t) => ({
    ...t,
    displayName: buildTeammateDisplayName(t),
  })),
);
